// Столбцы «кто больше»: сравнить объекты, типы, людей между собой.
//
// ## Чем отличается от полосы структуры
//
// «Из чего складывается сумма» и «кто больше» — разные вопросы, и у них разные виды. Полоса
// отвечает на первый: она показывает ДОЛИ одного целого. Столбцы отвечают на второй: они
// показывают величины, у которых общего целого может и не быть вовсе — восемь объектов из
// шестидесяти, пять должников из ста.
//
// Столбцы горизонтальные, потому что подписи у нас длинные: «ООО «БурСервис»» под
// вертикальным столбцом встаёт наискось или режется, а слева от горизонтального читается
// как обычная строка списка.
//
// ## Величина написана НА СТОЛБЦЕ, а оси нет
//
// Так было не сразу. Первый живой кадр 08.09.2026 показал восемь синих столбцов и ось внизу
// с делениями «0 · 7 тыс · 14 тыс · 26,1 тыс»: чтобы узнать, сколько должен Житневев, надо
// было навести курсор — то есть догадаться, что там что-то есть, и уметь этим пользоваться.
// Ось отвечает «примерно сколько», а человеку на этом экране нужно точное число, и нужно
// сразу. Подпись на конце столбца отвечает всем и без единого движения.
//
// Величину кодирует ДЛИНА, заливка плоская: градиент добавил бы вторую, ложную ось
// (`ui-views.md`, «в графиках нет градиента»).
//
// Вид взят из набора AlignUI (`widget-major-expenses` и его `vertical-bar-chart`).

import VerticalBarChart from '@/shared/ui/chart-vertical-bar';
import type { ChartConfig } from '@/shared/ui/chart';

/**
 * Цвет у всех столбцов ОДИН.
 *
 * Сравнивают здесь длину, и разноцветные столбцы заставляли бы искать смысл в цвете, которого
 * в нём нет: у восьми должников нет восьми разных смыслов, это один и тот же факт восемь раз.
 * В наборе тот же вид раскрашен по-разному, но там столбцы — КАТЕГОРИИ расхода, и цвет у них
 * значит ровно то же, что в легенде рядом.
 *
 * Первый столбец выделять светлее тоже нельзя: на тёмной теме светлый оттенок семейства
 * становится тёмно-синим и почти сливается с полем карточки — проверено снимком 08.09.2026.
 * Кто больше всех, видно по длине, а она у первого и так наибольшая.
 */
const ЦВЕТ_СТОЛБЦА = 'hsl(var(--information-base))';

export interface СтрокаСравнения {
  id: string;
  /** Короткое имя для подписи слева. Длинное здесь не поместится. */
  name: string;
  value: number;
}

export interface СравнениеProps {
  rows: СтрокаСравнения[];
  /** Как подписать величину: деньги, люди, часы. Она же пишется на конце столбца. */
  formatValue: (value: number) => string;
  /** Ширина колонки подписей. Имена объектов длиннее имён типов долга. */
  yAxisWidth?: number;
}

export function Сравнение({ rows, formatValue, yAxisWidth = 120 }: СравнениеProps) {
  const config: ChartConfig = Object.fromEntries(
    rows.map((строка) => [строка.id, { label: строка.name, color: ЦВЕТ_СТОЛБЦА }]),
  );
  // Высота считается из числа столбцов: у набора она зашита в 86 точек под три столбца, и
  // восемь при такой высоте налезают друг на друга. Двадцать четыре точки на столбец — это
  // столбец в шестнадцать и восемь зазора, как в исходнике при трёх.
  const высота = rows.length * 24 + 8;

  return (
    <VerticalBarChart
      data={rows.map((строка) => ({ id: строка.id, name: строка.name, value: строка.value }))}
      chartConfig={config}
      formatValue={formatValue}
      showValues
      height={высота}
      yAxisWidth={yAxisWidth}
    />
  );
}
